/**
 * Trend charts for the CashFlow Statement module.
 * Shows cashflow over time, passive vs earned income split,
 * net worth progression, and financial freedom % trend.
 */

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, LineChart, Line, ReferenceLine, Legend,
} from 'recharts';
import type { DerivedMetrics } from '../../types/cashflow';
import { formatCurrency, formatPct, formatMonth } from '../../utils/cashflowCalc';

interface Props {
  allMetrics: DerivedMetrics[];
  summaries: Array<{ month: string; earned_income: number; passive_income: number; portfolio_income: number }>;
  selectedMonth: string;
  onSelectMonth: (month: string) => void;
}

export default function TrendCharts({ allMetrics, summaries, selectedMonth, onSelectMonth }: Props) {
  const cashflowData = allMetrics.map((m) => ({
    month: formatMonth(m.month),
    rawMonth: m.month,
    cashflow: m.monthlyCashflow,
    fill: m.monthlyCashflow >= 0 ? '#48bb78' : '#f56565',
  }));

  const incomeData = summaries.map((s) => ({
    month: formatMonth(s.month),
    rawMonth: s.month,
    Earned: s.earned_income,
    Passive: s.passive_income,
    Portfolio: s.portfolio_income,
  }));

  const netWorthData = allMetrics.map((m) => ({
    month: formatMonth(m.month),
    rawMonth: m.month,
    netWorth: m.netWorth,
  }));

  const freedomData = allMetrics.map((m) => ({
    month: formatMonth(m.month),
    rawMonth: m.month,
    freedom: Math.round(m.financialFreedomPct * 100),
  }));

  const chartStyle = { background: '#fff', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' };

  return (
    <div>
      {/* Monthly History navigator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--spacing-lg)', flexWrap: 'wrap' }}>
        {allMetrics.map((m) => (
          <button
            key={m.month}
            onClick={() => onSelectMonth(m.month)}
            style={{
              padding: '4px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-border)',
              background: m.month === selectedMonth ? 'var(--color-accent)' : '#fff',
              color: m.month === selectedMonth ? '#fff' : 'var(--color-text-primary)',
              fontWeight: m.month === selectedMonth ? 700 : 400,
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            {formatMonth(m.month)}
          </button>
        ))}
      </div>

      {/* CashFlow bar chart */}
      <div style={chartStyle}>
        <h4 style={{ margin: '0 0 var(--spacing-md)', color: 'var(--color-text-primary)' }}>Monthly CashFlow</h4>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={cashflowData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [formatCurrency(Number(v)), 'CashFlow']} />
            <ReferenceLine y={0} stroke="#000" strokeWidth={1} />
            <Bar dataKey="cashflow" name="CashFlow" fill="#48bb78" radius={[4, 4, 0, 0]}
              label={false}
              isAnimationActive={true}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Income split area chart */}
      <div style={chartStyle}>
        <h4 style={{ margin: '0 0 var(--spacing-md)', color: 'var(--color-text-primary)' }}>Income Split</h4>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={incomeData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [formatCurrency(Number(v))]} />
            <Legend />
            <Area type="monotone" dataKey="Earned" stackId="1" stroke="#4299e1" fill="#bee3f8" />
            <Area type="monotone" dataKey="Passive" stackId="1" stroke="#48bb78" fill="#c6f6d5" />
            <Area type="monotone" dataKey="Portfolio" stackId="1" stroke="#9f7aea" fill="#e9d8fd" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Net worth line chart */}
      <div style={chartStyle}>
        <h4 style={{ margin: '0 0 var(--spacing-md)', color: 'var(--color-text-primary)' }}>Net Worth</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={netWorthData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [formatCurrency(Number(v)), 'Net Worth']} />
            <Line type="monotone" dataKey="netWorth" name="Net Worth" stroke="#f6ad55" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Financial Freedom % line chart */}
      <div style={chartStyle}>
        <h4 style={{ margin: '0 0 var(--spacing-md)', color: 'var(--color-text-primary)' }}>Financial Freedom %</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={freedomData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [`${v}%`, 'Freedom']} />
            <ReferenceLine y={100} stroke="#f6ad55" strokeDasharray="4 4" label={{ value: '🎯 100% — Free!', position: 'insideTopRight', fontSize: 11 }} />
            <Line type="monotone" dataKey="freedom" name="Freedom %" stroke="#48bb78" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
